import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import cx from "classnames";
import { Hooks } from "@xt/xtbase";
import styles from "./index.module.less";

const { useTranslation } = Hooks;

const EMOJI_WARN = "⚠️";
const EMOJI_BRAIN = "🧠";
const EMOJI_OK = "✅";
const EMOJI_NO = "❌";
const EMOJI_ROCKET = "🚀";
const EMOJI_GEM = "💎";

function getQuantOutputPayload(item) {
  if (!item || typeof item !== "object") return null;
  const raw = item.outputText ?? item.output_text;
  if (raw == null) return null;
  const str = String(raw).trim();
  if (!str) return null;
  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function pickStrategyName(strategyNames, locale) {
  if (!Array.isArray(strategyNames) || strategyNames.length === 0) return null;
  const raw = (locale || "en").toLowerCase().replace(/_/g, "-");
  const primary = raw.split("-")[0];
  const langMap = { cn: "zh", zh: "zh" };
  const preferred = langMap[primary] || primary;
  const order = [preferred, "en", "zh", "pt", "ar"];
  const codes = [...new Set(order)];
  for (const code of codes) {
    const hit = strategyNames.find((x) => String(x?.language || "").toLowerCase().startsWith(code));
    if (hit?.name) return hit.name;
  }
  return strategyNames.find((x) => x?.name)?.name || null;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** outputText 按 HTML 片段渲染：换行 → &lt;br /&gt;（内容先转义，避免 XSS） */
function outputTextToHtml(raw) {
  if (raw == null || !String(raw).trim()) return "";
  return escapeHtml(String(raw)).replace(/\r\n|\r|\n/g, "<br />");
}

/** outputText 为 JSON 字符串时 parse 后取 content 展示；解析失败或非对象则退回原字符串 */
function getMessageDisplayText(item) {
  if (!item || typeof item !== "object") return "";
  const raw = item.outputText ?? item.output_text;
  if (raw == null) return "";
  const str = String(raw).trim();
  if (!str) return "";
  const payload = getQuantOutputPayload(item);
  if (payload && payload.content != null) {
    return String(payload.content);
  }
  return str;
}

/** 去掉正文开头已带的图例 emoji（可多次），避免与 leadingEmojiForItem 再拼一层 */
function stripKnownLeadingEmojis(s) {
  let t = String(s || "").replace(/^\s+/, "");
  const prefixes = [EMOJI_WARN, EMOJI_BRAIN, EMOJI_OK, EMOJI_NO, EMOJI_ROCKET, EMOJI_GEM];
  let changed = true;
  while (changed) {
    changed = false;
    for (const p of prefixes) {
      if (t.startsWith(p)) {
        t = t.slice(p.length).replace(/^\s+/, "");
        changed = true;
        break;
      }
    }
  }
  return t;
}

/** 与 bubble 配色一致：红⚠️ 蓝🧠 绿✅ 橙🚀 紫💎（Figma 10397:34824） */
function leadingEmojiForItem(item) {
  const role = normalizeQuantRole(item.role);
  if (role === "infobot") return EMOJI_WARN;
  if (role === "traderbot") return EMOJI_BRAIN;
  if (role === "riskbot" || role === "risk") {
    const verdict = String(item?.riskVerdict || "").toLowerCase();
    if (verdict === "approve") return EMOJI_OK;
    if (verdict === "reject") return EMOJI_NO;
    return EMOJI_WARN;
  }
  const execPres = getExecBotPresentation(item);
  if (execPres === "uncard") return EMOJI_BRAIN;
  if (execPres === "card") {
    const mode = resolveExecBotStage(item);
    if (mode === "open") return EMOJI_ROCKET;
    if (mode === "close") return EMOJI_GEM;
    return "";
  }
  if (Number(item.roundClosed) === 1) return EMOJI_GEM;
  if (Number(item.roundOpened) === 1) return EMOJI_ROCKET;
  return EMOJI_BRAIN;
}

function normalizeQuantRole(role) {
  return String(role || "")
    .replace(/\s/g, "")
    .toLowerCase();
}

/**
 * ExecBot-card：结构化开/关仓卡片；ExecBot-uncard：与 traderbot 等一致纯文案气泡。
 * 兼容旧接口 role 仅为 execbot 时视为 card。
 */
function getExecBotPresentation(item) {
  const r = normalizeQuantRole(item?.role);
  if (r === "execbot-uncard") return "uncard";
  if (r === "execbot-card") return "card";
  if (r === "execbot") return "card";
  return null;
}

/** 接口可能返回 displayTime / displaytime / display_time（库表列为 displaytime） */
function pickDisplayTime(item) {
  if (!item || typeof item !== "object") return "";
  const raw = item.displayTime ?? item.displaytime ?? item.display_time;
  if (raw == null) return "";
  const s = String(raw).trim();
  return s;
}

function parseDisplayTimeToDate(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(raw).trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    if (n > 1e12) return new Date(n);
    if (n > 1e9) return new Date(n * 1000);
  }
  const d = new Date(s.replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 仅展示时刻：中文「14点06分」，其它语言「14:06」 */
function formatDisplayTimeShort(raw, locale) {
  const d = parseDisplayTimeToDate(raw);
  if (!d) return String(raw ?? "").trim();
  const h = d.getHours();
  const m = d.getMinutes();
  const loc = String(locale || "")
    .toLowerCase()
    .replace(/_/g, "-");
  const isZh = loc.startsWith("zh") || loc === "cn" || loc === "hk";
  if (isZh) {
    return `${h}点${String(m).padStart(2, "0")}分`;
  }
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function getExecBotStage(item) {
  const payload = getQuantOutputPayload(item);
  const stage = String(payload?.stage || "").toLowerCase();
  if (stage === "open" || stage === "close") return stage;
  return null;
}

/** 仅用于 ExecBot-card：解析开/关仓阶段（JSON stage → 顶层字段 → round 标记） */
function resolveExecBotStage(item) {
  const fromOutput = getExecBotStage(item);
  if (fromOutput === "open" || fromOutput === "close") return fromOutput;

  const top = String(item?.stage ?? item?.execStage ?? item?.exec_stage ?? "").toLowerCase();
  if (top === "open" || top === "close") return top;

  const roundClosed = Number(item?.roundClosed ?? item?.round_closed);
  if (roundClosed === 1) return "close";
  const roundOpened = Number(item?.roundOpened ?? item?.round_opened);
  if (roundOpened === 1) return "open";

  return null;
}

function formatExecSymbol(symbol) {
  if (symbol == null || symbol === "") return "—";
  let s = String(symbol).replace(/_cfd$/i, "").toLowerCase();
  const parts = s.split("_").filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].toUpperCase()}/${parts[1].toUpperCase()}`;
  }
  return s.toUpperCase();
}

function sortExecOrdersForDisplay(orders) {
  return [...orders].sort((a, b) => {
    const da = String(a.direction || "").toUpperCase();
    const db = String(b.direction || "").toUpperCase();
    if (da === "SHORT" && db !== "SHORT") return -1;
    if (da !== "SHORT" && db === "SHORT") return 1;
    return 0;
  });
}

/** 接口可能返回 camelCase / snake_case */
function pickOrderInfo(item) {
  if (!item || typeof item !== "object") return null;
  const raw = item.orderInfo ?? item.order_info;
  return raw && typeof raw === "object" ? raw : null;
}

/**
 * 开仓：用 orderInfo 拆成与旧 orders[] 相同结构的展示行（关仓仍用 orders）。
 * openDirection=BOTH 视为多空双开，拆成 LONG + SHORT 两行，字段共用 symbol/leverage/openPrice/quantity。
 */
function expandOrderInfoToExecOrders(orderInfo) {
  if (!orderInfo || typeof orderInfo !== "object") return [];
  const symbol = orderInfo.symbol ?? orderInfo.Symbol;
  if (symbol == null || String(symbol).trim() === "") return [];

  const openDirection = String(orderInfo.openDirection ?? orderInfo.open_direction ?? "BOTH")
    .toUpperCase()
    .trim();
  const leverage = orderInfo.leverage ?? orderInfo.Leverage;
  const quantity = orderInfo.quantity ?? orderInfo.Quantity;
  const openPrice = orderInfo.openPrice ?? orderInfo.open_price;

  const base = { symbol, leverage, quantity, openPrice };

  if (openDirection === "LONG") {
    return [{ ...base, direction: "LONG" }];
  }
  if (openDirection === "SHORT" || openDirection === "SELL") {
    return [{ ...base, direction: "SHORT" }];
  }
  // BOTH 或未识别：按双开展示
  return [
    { ...base, direction: "LONG" },
    { ...base, direction: "SHORT" },
  ];
}

function getSortedExecOrdersForItem(item, execMode) {
  if (execMode === "close") {
    return sortExecOrdersForDisplay(Array.isArray(item.orders) ? item.orders : []);
  }
  if (execMode === "open") {
    const fromInfo = expandOrderInfoToExecOrders(pickOrderInfo(item));
    if (fromInfo.length > 0) {
      return sortExecOrdersForDisplay(fromInfo);
    }
    return sortExecOrdersForDisplay(Array.isArray(item.orders) ? item.orders : []);
  }
  return [];
}

function formatRoiPercent(pct, leverage = 1) {
  if (pct == null || pct === "") return "—";
  const n = Number(pct);
  const lev = Number(leverage);
  if (!Number.isFinite(n) || !Number.isFinite(lev)) return "—";
  const value = n * lev * 100;
  const normalized = Number.isInteger(value) ? value : Number(value.toFixed(2));
  const sign = normalized > 0 ? "+" : "";
  return `${sign}${normalized}%`;
}

/** SHORT / LONG 前设计稿折线图标（非三角） */
function ExecSideTrendIcon({ isShort }) {
  if (isShort) {
    return (
      <svg className={styles.sideTrendIcon} width="14" height="9" viewBox="0 0 14 9" fill="none" aria-hidden>
        <path
          d="M13.3001 3.19411C13.1145 3.19411 12.9365 3.26785 12.8052 3.3991C12.674 3.53034 12.6003 3.70836 12.6003 3.89397V5.70659L8.48511 1.59145C8.35398 1.4611 8.1766 1.38793 7.99171 1.38793C7.80682 1.38793 7.62944 1.4611 7.49831 1.59145L5.04182 4.04794L1.19962 0.205732C1.06783 0.073947 0.889091 -8.96454e-05 0.702719 -8.96454e-05C0.516346 -8.96454e-05 0.337607 0.073947 0.205822 0.205732C0.0740362 0.337517 0 0.516256 0 0.702629C0 0.889002 0.0740362 1.06774 0.205822 1.19953L4.54492 5.53863C4.67605 5.66898 4.85343 5.74214 5.03832 5.74214C5.22321 5.74214 5.40059 5.66898 5.53172 5.53863L7.98821 3.08214L11.6065 6.70039H9.80084C9.61522 6.70039 9.43721 6.77412 9.30596 6.90537C9.17471 7.03662 9.10098 7.21463 9.10098 7.40024C9.10098 7.58586 9.17471 7.76387 9.30596 7.89511C9.43721 8.02636 9.61522 8.1001 9.80084 8.1001H13.3001C13.4857 8.1001 13.6637 8.02636 13.795 7.89511C13.9262 7.76387 14 7.58586 14 7.40024V3.90097C14.0009 3.80847 13.9835 3.71671 13.9487 3.63099C13.914 3.54527 13.8626 3.4673 13.7975 3.40156C13.7324 3.33583 13.6549 3.28366 13.5696 3.24805C13.4842 3.21244 13.3926 3.19411 13.3001 3.19411Z"
          fill="#BF4545"
        />
      </svg>
    );
  }
  return (
    <svg className={styles.sideTrendIcon} width="14" height="9" viewBox="0 0 14 9" fill="none" aria-hidden>
      <path
        d="M13.3001 4.90598C13.1145 4.90598 12.9365 4.83225 12.8052 4.701C12.674 4.56975 12.6003 4.39174 12.6003 4.20613V2.3935L8.48511 6.50865C8.35398 6.639 8.1766 6.71216 7.99171 6.71216C7.80682 6.71216 7.62944 6.639 7.49831 6.50865L5.04182 4.05216L1.19962 7.89437C1.06783 8.02615 0.889091 8.10019 0.702719 8.10019C0.516346 8.10019 0.337607 8.02615 0.205822 7.89437C0.0740362 7.76258 0 7.58384 0 7.39747C0 7.2111 0.0740362 7.03236 0.205822 6.90057L4.54492 2.56147C4.67605 2.43112 4.85343 2.35796 5.03832 2.35796C5.22321 2.35796 5.40059 2.43112 5.53172 2.56147L7.98821 5.01796L11.6065 1.39971H9.80084C9.61522 1.39971 9.43721 1.32598 9.30596 1.19473C9.17471 1.06348 9.10098 0.885468 9.10098 0.699855C9.10098 0.514242 9.17471 0.336231 9.30596 0.204983C9.43721 0.0737344 9.61522 0 9.80084 0H13.3001C13.4857 0 13.6637 0.0737344 13.795 0.204983C13.9262 0.336231 14 0.514242 14 0.699855V4.19913C14.0009 4.29162 13.9835 4.38338 13.9487 4.4691C13.914 4.55482 13.8626 4.6328 13.7975 4.69853C13.7324 4.76426 13.6549 4.81644 13.5696 4.85205C13.4842 4.88766 13.3926 4.90599 13.3001 4.90598Z"
        fill="#45BF59"
      />
    </svg>
  );
}

function ExecBotOrderColumn({ order, mode, t }) {
  const sym = formatExecSymbol(order.symbol);
  const dir = String(order.direction || "").toUpperCase();
  const isShort = dir === "SHORT" || dir === "SELL";
  const lev = order.leverage != null && order.leverage !== "" ? order.leverage : "—";
  const roiRaw = order.roiPct ?? order.roi_pct;
  const levNum = Number(lev);
  const roiNum = roiRaw != null && roiRaw !== "" ? Number(roiRaw) * levNum * 100 : NaN;
  const roiText = mode === "close" ? formatRoiPercent(roiRaw, lev) : null;
  const roiNeg = Number.isFinite(roiNum) && roiNum < 0;

  return (
    <div className={styles.posCol}>
      <div className={styles.posHead}>
        <ExecSideTrendIcon isShort={isShort} />
        <span className={isShort ? styles.sideShort : styles.sideLong}>{isShort ? "SHORT" : "LONG"}</span>
        <span className={styles.pair}>{sym}</span>
      </div>
      {mode === "open" ? (
        <div className={styles.posSub}>
          <div className={styles.posSubLine}>{t("aicopy.aiLabLeverageLabel", { n: lev })}</div>
          <div className={styles.posSubLine}>{t("aicopy.aiLabMarketPrice")}</div>
        </div>
      ) : (
        <div className={styles.roiRow}>
          <span className={styles.roiLabel}>{t("aicopy.aiLabRoiLabel")}</span>
          <span className={cx(styles.roiValue, roiNeg && styles.roiValueNeg)}>{roiText}</span>
        </div>
      )}
    </div>
  );
}

function bubbleClassForItem(item) {
  const role = normalizeQuantRole(item.role);
  if (role === "infobot") return `${styles.bubble} ${styles.bubbleInfoBot}`;
  if (role === "traderbot") return `${styles.bubble} ${styles.bubbleTradeBot}`;
  if (role === "riskbot" || role === "risk") {
    const verdict = String(item?.riskVerdict || "").toLowerCase();
    if (verdict === "approve") return `${styles.bubble} ${styles.bubbleOk}`;
    return `${styles.bubble} ${styles.bubbleRiskBot}`;
  }
  const execPres = getExecBotPresentation(item);
  if (execPres === "uncard") return `${styles.bubble} ${styles.bubbleTradeBot}`;
  if (execPres === "card") {
    const mode = resolveExecBotStage(item);
    if (mode === "open") return `${styles.bubble} ${styles.bubbleOpen}`;
    if (mode === "close") return `${styles.bubble} ${styles.bubbleClose}`;
    return `${styles.bubble} ${styles.bubbleTradeBot}`;
  }
  if (Number(item.roundClosed) === 1) return `${styles.bubble} ${styles.bubbleClose}`;
  if (Number(item.roundOpened) === 1) return `${styles.bubble} ${styles.bubbleOpen}`;
  return `${styles.bubble} ${styles.bubbleInfoBot}`;
}

function getFeedDisplayName(item, locale, t) {
  const role = normalizeQuantRole(item?.role);
  if (role === "infobot") return t("aicopy.aiLabFeedNameInfoBot");
  if (role === "riskbot" || role === "risk") return t("aicopy.aiLabFeedNameRiskBot");
  return pickStrategyName(item?.strategyNames, locale) || "--";
}

function FeedRowHeader({ avatarSrc, name, time }) {
  return (
    <div className={styles.feedMeta}>
      {avatarSrc ? (
        <div className={styles.feedAvatar}>
          <img src={avatarSrc} alt="" className={styles.feedAvatarImg} />
        </div>
      ) : null}
      <span className={styles.feedName}>{name}</span>
      {time ? <span className={styles.feedTime}>{time}</span> : null}
    </div>
  );
}

/**
 * AI 联合实验卡片 — Quant 消息流（先 HTTP 全量旧→新，再 WS 尾巴与增量追加在底部）
 * @param {Array} items
 * @param {boolean} loading HTTP 全量拉取中占位
 */
export default function AiLabJointLabCard({
  items = [],
  loading = false,
  avatarSrc,
  onCopyTrade,
  strategyOptions = [],
  activeStrategyId = "",
  onStrategyChange,
} = {}) {
  const t = useTranslation();
  const router = useRouter();
  const locale = router?.locale || "en";
  const list = Array.isArray(items) ? items : [];
  const strategyTabs = Array.isArray(strategyOptions) ? strategyOptions : [];
  const [strategyDropdownOpen, setStrategyDropdownOpen] = useState(false);
  const strategySelectRef = useRef(null);

  const activeStrategy =
    strategyTabs.find((opt) => String(opt.strategyId) === String(activeStrategyId)) || strategyTabs[0] || null;

  const feedScrollRef = useRef(null);
  const prevListLenRef = useRef(0);

  useEffect(() => {
    if (!strategyDropdownOpen) return undefined;
    const onDocClick = (e) => {
      if (strategySelectRef.current?.contains(e.target)) return;
      setStrategyDropdownOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [strategyDropdownOpen]);

  useEffect(() => {
    const el = feedScrollRef.current;
    if (!el || list.length === 0) {
      prevListLenRef.current = list.length;
      return;
    }
    if (list.length >= prevListLenRef.current) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
    prevListLenRef.current = list.length;
  }, [list]);

  const handleCopy = (e, item) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const strategyId = item?.strategyId;
    if (strategyId != null && strategyId !== "") {
      router.push(`/aicopy/${strategyId}`);
      return;
    }
    if (typeof onCopyTrade === "function") onCopyTrade(item);
  };

  return (
    <div className={`${styles.card} ${loading ? styles.cardLoading : ""}`}>
      <div className={styles.headRow}>
        <div className={styles.liveBadge} aria-hidden>
          <span className={styles.liveBadgeDot} />
          <span>{t("aicopy.aiLabLiveBadge")}</span>
        </div>
        <h2 className={styles.headTitle}>{t("aicopy.aiLabTitle")}</h2>
        {strategyTabs.length > 0 && (
          <div
            ref={strategySelectRef}
            className={styles.strategySelectWrap}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.strategySelect}
              aria-expanded={strategyDropdownOpen}
              aria-haspopup="listbox"
              aria-label={t("aicopy.aiLabStrategyFilter")}
              onClick={() => setStrategyDropdownOpen((v) => !v)}
            >
              <span className={styles.strategySelectText}>{activeStrategy?.label || "—"}</span>
              <span className={cx(styles.strategySelectArrow, strategyDropdownOpen && styles.strategySelectArrowOpen)}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path
                    d="M4 5.33L7 8.33L10 5.33"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            {strategyDropdownOpen && (
              <div className={styles.strategyDropdown} role="listbox">
                {strategyTabs.map((opt) => {
                  const active = String(activeStrategyId) === String(opt.strategyId);
                  return (
                    <button
                      key={opt.strategyId}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={cx(styles.strategyOption, active && styles.strategyOptionActive)}
                      onClick={() => {
                        onStrategyChange && onStrategyChange(String(opt.strategyId));
                        setStrategyDropdownOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {loading && list.length === 0 && (
        <div className={styles.emptyHint}>{t("aicopy.aiLabHistoryLoading")}</div>
      )}

      {!loading && list.length === 0 && (
        <div className={styles.emptyHint}>{t("aicopy.aiLabHistoryEmpty")}</div>
      )}

      {list.length > 0 && (
        <div className={styles.feedScrollWrap}>
          <div
            ref={feedScrollRef}
            className={styles.feedScroll}
            role="log"
            aria-label={t("aicopy.aiLabTitle")}
          >
            {list.map((item) => {
        const name = getFeedDisplayName(item, locale, t);
        const timeRaw = pickDisplayTime(item);
        const time = timeRaw ? formatDisplayTimeShort(timeRaw, locale) : "";
        const displayPlain = stripKnownLeadingEmojis(getMessageDisplayText(item));
        const leadEmoji = leadingEmojiForItem(item);
        const outputHtml =
          displayPlain.trim() === ""
            ? ""
            : `${leadEmoji ? `${leadEmoji} ` : ""}${outputTextToHtml(displayPlain)}`;
        const execPresentation = getExecBotPresentation(item);
        const execMode = execPresentation === "card" ? resolveExecBotStage(item) : null;
        const useExecOrdersCard = execMode != null;
        const emptyBody = !useExecOrdersCard && !outputHtml.trim();
        const showFollow =
          execPresentation === "card" && (execMode === "open" || execMode === "close");
        const cardAvatarSrc = item.avatarUrl || "";
        const sortedExecOrders = getSortedExecOrdersForItem(item, execMode);

              return (
                <div
                  key={item.id != null ? item.id : `${name}-${outputHtml.slice(0, 20)}`}
                  className={styles.feedItem}
                >
                  <FeedRowHeader avatarSrc={cardAvatarSrc} name={name} time={time} />
                  <div className={bubbleClassForItem(item)}>
                    {useExecOrdersCard ? (
                      <div className={styles.execBotWrap}>
                        <p className={styles.execCardHeadline}>
                          {execMode === "open" ? t("aicopy.aiLabFeedOpenTitle") : t("aicopy.aiLabFeedCloseTitle")}
                        </p>
                        <div className={execMode === "open" ? styles.embedDark : styles.embedPurple}>
                          {sortedExecOrders.length > 0 ? (
                            <div className={styles.embedTwoCol}>
                              {sortedExecOrders.map((ord, idx) => (
                                <ExecBotOrderColumn key={`${ord.symbol}-${ord.direction}-${idx}`} order={ord} mode={execMode} t={t} />
                              ))}
                            </div>
                          ) : (
                            <div className={styles.embedTwoCol}>
                              <ExecBotOrderColumn order={{}} mode={execMode} t={t} />
                              <ExecBotOrderColumn order={{}} mode={execMode} t={t} />
                            </div>
                          )}
                          {showFollow && (
                            <button type="button" className={styles.copyBtn} onClick={(e) => handleCopy(e, item)}>
                              {t("aicopy.followNow")}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : emptyBody ? (
                      <div>—</div>
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: outputHtml }} />
                    )}
                    {!useExecOrdersCard && showFollow && (
                      <button type="button" className={styles.copyBtn} onClick={(e) => handleCopy(e, item)}>
                        {t("aicopy.followNow")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
